import { RuntimeBindings } from './RuntimeBindings';
import { standaloneRuntimeIds, type RuntimeGraphIds } from './RuntimeGraphIds';
import type { IChipInstanceModel, IConnectionModel, ITileManagerModel } from '@/interfaces/models';
import { TileInstance } from '@/models/tiles/TileInstance';
import { TileType } from '@/models/TileTypes';
import { SIInputMode } from '@/models/ChipModel';
import { deepClone } from '@/utils/clone';
import { LogicGraph } from './LogicGraph';
import { GraphManager } from './GraphManager';

/** Template builds own their tile IDs and embedded graphs independently of live simulation. */
export class TemplateGraphBuilder {
  constructor(private readonly ids: RuntimeGraphIds = standaloneRuntimeIds) {}
  private copies = new Map<string, { source: TileInstance; tile: TileInstance }>();
  private instances = new Map<IChipInstanceModel, IChipInstanceModel>();
  private manager?: GraphManager;

  rebuild(sourceManager: ITileManagerModel, connections: IConnectionModel | undefined, sourceInstances: IChipInstanceModel[]) {
    const steps = this.rebuildSteps(sourceManager, connections, sourceInstances);
    let result = steps.next();
    while (!result.done) result = steps.next();
    return result.value;
  }

  *rebuildSteps(sourceManager: ITileManagerModel, connections: IConnectionModel | undefined, sourceInstances: IChipInstanceModel[]) {
    const sourceTiles = sourceManager.tiles;
    const currentIds = new Set<string>(sourceTiles.map(tile => tile.id));
    for (const id of this.copies.keys()) if (!currentIds.has(id)) this.copies.delete(id);
    const tiles: TileInstance[] = [];
    for (const source of sourceTiles) {
      let entry = this.copies.get(source.id);
      if (!entry || entry.source !== source) {
        const tile = new TileInstance(source.context, source.tileType, source.globalTilePosition, { simulationOnly: true });
        tile.id = source.id;
        tile.logicNodeIds = undefined;
        entry = { source, tile };
        this.copies.set(source.id, entry);
      }
      const tile = entry.tile;
      tile.tileType = source.tileType;
      tile.globalTilePosition = source.globalTilePosition;
      tile.meta = deepClone(source.meta);
      tile.state = deepClone(source.state);
      tile.effectiveIoSides = source.effectiveIoSides && deepClone(source.effectiveIoSides);
      if (tile.tileType === TileType.SingleInterface && !tile.meta.partOfChip) {
        const mode = (tile.state as { inputMode?: SIInputMode }).inputMode;
        if (!mode || mode === SIInputMode.NONE) tile.tileType = TileType.Wire;
      }
      tiles.push(tile);
      if (tiles.length % 128 === 0) yield;
    }
    const currentInstances = new Set(sourceInstances);
    for (const source of this.instances.keys()) if (!currentInstances.has(source)) this.instances.delete(source);
    const chips = sourceInstances.map(source => {
      let copy = this.instances.get(source);
      if (!copy) {
        const local = { logicGraph: new LogicGraph(this.ids), templateToInstanceIdMap: new RuntimeBindings() };
        copy = new Proxy({} as IChipInstanceModel, {
          get: (_target, key) => key in local ? Reflect.get(local, key) : Reflect.get(source, key, source),
          set: (_target, key, value) => Reflect.set(local, key, value),
        });
        this.instances.set(source, copy);
      }
      return copy;
    });
    const manager = new Proxy({} as ITileManagerModel, { get: (_target, key) => {
      if (key === 'tiles') return tiles;
      if (key === 'get') return (point: import('@/interfaces/models').IPoint) => {
        const source = sourceManager.get(point);
        return source && this.copies.get(source.id)?.tile;
      };
      return Reflect.get(sourceManager, key, sourceManager);
    } });
    this.manager ??= new GraphManager(manager, connections, chips, this.ids);
    this.manager.tileManager = manager;
    this.manager.connectionModel = connections;
    this.manager.chipInstances = chips;
    yield* this.manager.rebuildGraphSteps();
    const graphManager = this.manager;
    const instanceNodeMaps = new Map(chips.map((chip) => [
      chip.id,
      new Map([...chip.templateToInstanceIdMap].map(([templateId, id]) => [
        templateId, graphManager.resolveNodeId(id),
      ])),
    ]));
    return {
      graph: graphManager.graph, tiles,
      byId: new Map(tiles.map(tile => [tile.id, tile])),
      instanceNodeMaps,
    };
  }
}
